/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Pure Graph Domain Operations
   Immutable transformations on CanonicalGraph & RuntimeGraphIndexes.
   ═══════════════════════════════════════════════════════════════ */

import type {
  CanonicalGraph,
  DomainGraphState,
  EdgeId,
  EdgeInstance,
  NodeId,
  NodeInstance,
  PortId,
  Position,
} from './types';
import { generateStableId } from './id';
import { buildRuntimeIndexes } from './indexes';
import { validateConnection } from './validation';

/**
 * Creates a brand new empty domain graph state.
 */
export function createEmptyGraph(name = 'Untitled Studio Graph'): DomainGraphState {
  const now = Date.now();
  const canonical: CanonicalGraph = {
    id: generateStableId('graph'),
    name,
    version: 1,
    nodes: {},
    edges: {},
    createdAt: now,
    modifiedAt: now,
  };

  return {
    canonical,
    indexes: buildRuntimeIndexes(canonical),
  };
}

/**
 * Re-indexes an existing canonical graph into a full DomainGraphState.
 */
export function createDomainState(canonical: CanonicalGraph): DomainGraphState {
  return {
    canonical,
    indexes: buildRuntimeIndexes(canonical),
  };
}

/**
 * Purely adds a node to the graph state.
 */
export function addNode(state: DomainGraphState, node: NodeInstance): DomainGraphState {
  const updatedNodes = {
    ...state.canonical.nodes,
    [node.id]: node,
  };

  const updatedCanonical: CanonicalGraph = {
    ...state.canonical,
    nodes: updatedNodes,
    modifiedAt: Date.now(),
  };

  return {
    canonical: updatedCanonical,
    indexes: buildRuntimeIndexes(updatedCanonical),
  };
}

/**
 * Purely removes a node and all edges connected to it.
 */
export function removeNode(state: DomainGraphState, nodeId: NodeId): DomainGraphState {
  if (!state.canonical.nodes[nodeId]) {
    return state;
  }

  const updatedNodes = { ...state.canonical.nodes };
  delete updatedNodes[nodeId];

  // Remove any edges connected to this node
  const updatedEdges = { ...state.canonical.edges };
  for (const edge of Object.values(state.canonical.edges)) {
    if (edge.sourceNodeId === nodeId || edge.targetNodeId === nodeId) {
      delete updatedEdges[edge.id];
    }
  }

  const updatedCanonical: CanonicalGraph = {
    ...state.canonical,
    nodes: updatedNodes,
    edges: updatedEdges,
    modifiedAt: Date.now(),
  };

  return {
    canonical: updatedCanonical,
    indexes: buildRuntimeIndexes(updatedCanonical),
  };
}

/**
 * Purely updates a node's canvas position.
 * Does not require full index rebuild since graph topology is unchanged.
 */
export function updateNodePosition(
  state: DomainGraphState,
  nodeId: NodeId,
  position: Position
): DomainGraphState {
  const node = state.canonical.nodes[nodeId];
  if (!node) return state;

  const updatedNode: NodeInstance = {
    ...node,
    position,
  };

  const updatedCanonical: CanonicalGraph = {
    ...state.canonical,
    nodes: {
      ...state.canonical.nodes,
      [nodeId]: updatedNode,
    },
    modifiedAt: Date.now(),
  };

  return {
    canonical: updatedCanonical,
    indexes: state.indexes, // Topology unchanged
  };
}

/**
 * Purely updates a node's parameter value.
 * Topology unchanged.
 */
export function updateNodeParam(
  state: DomainGraphState,
  nodeId: NodeId,
  paramId: string,
  value: unknown
): DomainGraphState {
  const node = state.canonical.nodes[nodeId];
  if (!node) return state;

  const updatedNode: NodeInstance = {
    ...node,
    params: {
      ...node.params,
      [paramId]: value,
    },
  };

  const updatedCanonical: CanonicalGraph = {
    ...state.canonical,
    nodes: {
      ...state.canonical.nodes,
      [nodeId]: updatedNode,
    },
    modifiedAt: Date.now(),
  };

  return {
    canonical: updatedCanonical,
    indexes: state.indexes, // Topology unchanged
  };
}

/**
 * Purely updates a node's metadata label.
 */
export function renameNode(
  state: DomainGraphState,
  nodeId: NodeId,
  label: string
): DomainGraphState {
  const node = state.canonical.nodes[nodeId];
  if (!node) return state;

  const updatedNode: NodeInstance = {
    ...node,
    meta: {
      ...node.meta,
      label,
    },
  };

  const updatedCanonical: CanonicalGraph = {
    ...state.canonical,
    nodes: {
      ...state.canonical.nodes,
      [nodeId]: updatedNode,
    },
    modifiedAt: Date.now(),
  };

  return {
    canonical: updatedCanonical,
    indexes: state.indexes,
  };
}

/**
 * Purely toggles a node's collapsed visual state.
 */
export function toggleNodeCollapsed(
  state: DomainGraphState,
  nodeId: NodeId
): DomainGraphState {
  const node = state.canonical.nodes[nodeId];
  if (!node) return state;

  const updatedNode: NodeInstance = {
    ...node,
    collapsed: !node.collapsed,
  };

  const updatedCanonical: CanonicalGraph = {
    ...state.canonical,
    nodes: {
      ...state.canonical.nodes,
      [nodeId]: updatedNode,
    },
    modifiedAt: Date.now(),
  };

  return {
    canonical: updatedCanonical,
    indexes: state.indexes,
  };
}

/**
 * Validates and adds an edge between source and target ports.
 */
export function addEdge(
  state: DomainGraphState,
  sourceNodeId: NodeId,
  sourcePortId: PortId,
  targetNodeId: NodeId,
  targetPortId: PortId,
  explicitEdgeId?: EdgeId
): { state: DomainGraphState; edgeId?: EdgeId; error?: string } {
  const validation = validateConnection(
    state.canonical,
    state.indexes,
    sourceNodeId,
    sourcePortId,
    targetNodeId,
    targetPortId
  );

  if (!validation.valid) {
    return { state, error: validation.reason || 'Invalid connection' };
  }

  const edgeId = explicitEdgeId || generateStableId('edge');
  const edge: EdgeInstance = {
    id: edgeId,
    sourceNodeId,
    sourcePortId,
    targetNodeId,
    targetPortId,
  };

  const updatedCanonical: CanonicalGraph = {
    ...state.canonical,
    edges: {
      ...state.canonical.edges,
      [edgeId]: edge,
    },
    modifiedAt: Date.now(),
  };

  return {
    state: {
      canonical: updatedCanonical,
      indexes: buildRuntimeIndexes(updatedCanonical),
    },
    edgeId,
  };
}

/**
 * Purely removes an edge by ID.
 */
export function removeEdge(state: DomainGraphState, edgeId: EdgeId): DomainGraphState {
  if (!state.canonical.edges[edgeId]) {
    return state;
  }

  const updatedEdges = { ...state.canonical.edges };
  delete updatedEdges[edgeId];

  const updatedCanonical: CanonicalGraph = {
    ...state.canonical,
    edges: updatedEdges,
    modifiedAt: Date.now(),
  };

  return {
    canonical: updatedCanonical,
    indexes: buildRuntimeIndexes(updatedCanonical),
  };
}

/**
 * Purely toggles or sets a node's bypassed execution state.
 */
export function toggleNodeBypass(
  state: DomainGraphState,
  nodeId: NodeId,
  bypassed?: boolean
): DomainGraphState {
  const node = state.canonical.nodes[nodeId];
  if (!node) return state;

  const nextBypassed = bypassed !== undefined ? bypassed : !node.bypassed;
  const updatedNode: NodeInstance = {
    ...node,
    bypassed: nextBypassed,
  };

  const updatedCanonical: CanonicalGraph = {
    ...state.canonical,
    nodes: {
      ...state.canonical.nodes,
      [nodeId]: updatedNode,
    },
    modifiedAt: Date.now(),
  };

  return {
    canonical: updatedCanonical,
    indexes: state.indexes,
  };
}

/**
 * Adds or updates a frame for visual node grouping.
 */
export function addFrame(
  state: DomainGraphState,
  frame: import('./types').FrameInstance
): DomainGraphState {
  const updatedFrames = {
    ...(state.canonical.frames || {}),
    [frame.id]: frame,
  };

  const updatedCanonical: CanonicalGraph = {
    ...state.canonical,
    frames: updatedFrames,
    modifiedAt: Date.now(),
  };

  return {
    canonical: updatedCanonical,
    indexes: state.indexes,
  };
}

/**
 * Removes a frame by ID.
 */
export function removeFrame(
  state: DomainGraphState,
  frameId: import('./types').FrameId
): DomainGraphState {
  if (!state.canonical.frames || !state.canonical.frames[frameId]) {
    return state;
  }

  const updatedFrames = { ...state.canonical.frames };
  delete updatedFrames[frameId];

  const updatedCanonical: CanonicalGraph = {
    ...state.canonical,
    frames: updatedFrames,
    modifiedAt: Date.now(),
  };

  return {
    canonical: updatedCanonical,
    indexes: state.indexes,
  };
}

/**
 * Updates frame bounding coordinates.
 */
export function updateFrameBounds(
  state: DomainGraphState,
  frameId: import('./types').FrameId,
  bounds: import('./types').FrameInstance['bounds']
): DomainGraphState {
  const frame = state.canonical.frames?.[frameId];
  if (!frame) return state;

  return addFrame(state, {
    ...frame,
    bounds,
  });
}

/**
 * Updates a frame title label.
 */
export function renameFrame(
  state: DomainGraphState,
  frameId: import('./types').FrameId,
  title: string
): DomainGraphState {
  const frame = state.canonical.frames?.[frameId];
  if (!frame) return state;

  return addFrame(state, {
    ...frame,
    title,
  });
}
